/**
 * Pure floor-plan rules for table assignment on reservations.
 * No I/O, no DOM, no SDK calls — the repository, service and component
 * compose real data around this module (same split as
 * `admin-reservations-policy.js`).
 *
 * Fudo owns the physical floor plan; this app mirrors it into `mesas`
 * (docs/supabase/16-mesas-reservation-tables.sql). Fudo positions a table on a
 * DISCRETE GRID (`row`/`column`, non-negative integers), not on pixel
 * coordinates — so the map renders as a CSS grid, not as absolute positioning.
 */

/**
 * Mesa numbers hidden from the picker. Only `999`, the manual-testing table.
 *
 * `240` and `290` were hidden too, on the assumption that their odd numbering
 * meant they were not real tables. Fudo's own floor plan shows them as regular
 * (small) tables, so they belong on the map. A hidden mesa is still synced and
 * kept — deleting it would resurrect on the next sync, and it may hold
 * historical Fudo sales — just flagged `activa: false`.
 */
export const MESA_NUMEROS_OCULTAS = new Set([999]);

/** The three states a mesa can be in, relative to the reservation being edited. */
export const ESTADO_MESA = {
    LIBRE: 'libre',
    ASIGNADA: 'asignada',
    OCUPADA: 'ocupada',
};

/**
 * Comparable key for an area name across the Fudo/local spelling gap: Fudo
 * says `SALON`, this app says `Salón`. Accents and case are the only
 * differences, and normalizing both sides is what lets the sync map rooms to
 * areas without a hardcoded translation table that would rot the first time
 * a room is renamed.
 *
 * @param {string | null | undefined} name
 * @returns {string} normalized key, `''` for a missing name
 */
export function normalizeAreaKey(name) {
    if (name == null) return '';
    return String(name)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
}

/**
 * Maps a Fudo floor-plan payload (`GET /rooms` + `GET /tables`, JSON:API
 * shape) onto rows ready to upsert into `mesas`.
 *
 * A table whose room cannot be resolved to a local area is NOT silently
 * dropped: it comes back in `unmapped` so the sync can report it. Silently
 * skipping tables would make a renamed room look like an empty floor.
 *
 * @param {{
 *   rooms: Array<{ id: string, attributes: { name: string } }>,
 *   tables: Array<{ id: string, attributes: object, relationships: object }>,
 *   areaNames: string[]
 * }} params
 * @returns {{ mesas: Array<object>, unmapped: Array<{ id: number, numero: number, room: string }> }}
 */
export function mapFudoFloorPlan({ rooms, tables, areaNames }) {
    const areaByKey = new Map(areaNames.map((name) => [normalizeAreaKey(name), name]));
    const roomNameById = new Map(rooms.map((room) => [String(room.id), room.attributes?.name ?? '']));

    const mesas = [];
    const unmapped = [];

    for (const fudoTable of tables) {
        const attrs = fudoTable.attributes ?? {};
        const roomId = String(fudoTable.relationships?.room?.data?.id ?? '');
        const roomName = roomNameById.get(roomId) ?? '';
        const area = areaByKey.get(normalizeAreaKey(roomName));
        const numero = Number(attrs.number);

        if (!area) {
            unmapped.push({ id: Number(fudoTable.id), numero, room: roomName });
            continue;
        }

        mesas.push({
            id: Number(fudoTable.id),
            numero,
            area,
            grid_row: Number(attrs.row),
            grid_col: Number(attrs.column),
            shape: attrs.shape ?? null,
            size: attrs.size ?? null,
            activa: !MESA_NUMEROS_OCULTAS.has(numero),
        });
    }

    return { mesas, unmapped };
}

/**
 * Builds the floor-map view model for one reservation.
 *
 * `ocupacion` is every assignment for the reservation's TURNO — `(fecha,
 * momento)`, the occupancy unit enforced by the unique index in migration 16.
 * A mesa held by the reservation being edited reads `asignada` (and never
 * reports a holder — showing staff their own name back is noise); a mesa held
 * by any other reservation reads `ocupada` and carries that reservation's
 * label so the block message can name it.
 *
 * Grid size comes from the largest occupied row/column rather than from Fudo's
 * canvas: trailing empty rows would render as dead space.
 *
 * @param {{
 *   mesas: Array<object>,
 *   ocupacion: Array<{ mesa_id: number, reservation_id: string, label?: string }>,
 *   reservationId: string | null
 * }} params
 * @returns {{ filas: number, columnas: number, mesas: Array<object> }}
 */
export function buildFloorPlan({ mesas, ocupacion, reservationId }) {
    const activas = mesas.filter((mesa) => mesa.activa !== false);
    if (activas.length === 0) return { filas: 0, columnas: 0, mesas: [] };

    const holderByMesa = new Map(ocupacion.map((row) => [row.mesa_id, row]));

    const decorated = activas
        .map((mesa) => {
            const holder = holderByMesa.get(mesa.id);
            const mine = holder != null && reservationId != null && holder.reservation_id === reservationId;

            let estado = ESTADO_MESA.LIBRE;
            if (mine) estado = ESTADO_MESA.ASIGNADA;
            else if (holder) estado = ESTADO_MESA.OCUPADA;

            return {
                id: mesa.id,
                numero: mesa.numero,
                area: mesa.area,
                gridRow: mesa.grid_row,
                gridCol: mesa.grid_col,
                // CSS grid lines are 1-indexed; Fudo's rows/columns are 0-indexed.
                cssRow: mesa.grid_row + 1,
                cssCol: mesa.grid_col + 1,
                shape: mesa.shape ?? null,
                size: mesa.size ?? null,
                estado,
                ocupadaPor: estado === ESTADO_MESA.OCUPADA ? (holder.label ?? null) : null,
            };
        })
        .sort((a, b) => (a.gridRow - b.gridRow) || (a.gridCol - b.gridCol));

    return {
        filas: Math.max(...decorated.map((m) => m.gridRow)) + 1,
        columnas: Math.max(...decorated.map((m) => m.gridCol)) + 1,
        mesas: decorated,
    };
}

/**
 * Recomputes a plan's estados from the operator's LOCAL selection, so the map
 * reflects clicks that have not been saved yet.
 *
 * A mesa held by another reservation stays `ocupada` and is never repainted
 * from the selection — the operator cannot select it (it renders disabled), and
 * letting local state override it would show a table as free when it is not.
 *
 * @param {{ filas: number, columnas: number, mesas: Array<object> }} plan
 * @param {Iterable<number>} seleccion mesa ids currently picked
 * @returns {{ filas: number, columnas: number, mesas: Array<object> }}
 */
export function applySeleccion(plan, seleccion) {
    const elegidas = new Set(seleccion ?? []);

    return {
        ...plan,
        mesas: plan.mesas.map((mesa) => {
            if (mesa.estado === ESTADO_MESA.OCUPADA) return mesa;
            return {
                ...mesa,
                estado: elegidas.has(mesa.id) ? ESTADO_MESA.ASIGNADA : ESTADO_MESA.LIBRE,
                ocupadaPor: null,
            };
        }),
    };
}

/**
 * Diffs what a reservation currently holds against what the operator selected,
 * so saving touches only what actually changed.
 *
 * Selection is kept LOCAL in the modal until save, for both create and edit —
 * a brand-new reservation has no id to assign against, and making the two
 * flows behave differently would mean two sets of rules for the same screen.
 * Saving then reconciles: assign what was added, free what was removed, leave
 * the rest alone (re-assigning an untouched mesa would trip its own unique
 * index).
 *
 * @param {Array<{ id: number }>} actuales mesas already assigned
 * @param {Iterable<number>} seleccionadas mesa ids currently picked in the UI
 * @returns {{ aAsignar: number[], aLiberar: number[] }}
 */
export function reconcileAsignacion(actuales, seleccionadas) {
    const previas = new Set((actuales ?? []).map((mesa) => mesa.id));
    const nuevas = new Set(seleccionadas ?? []);

    return {
        aAsignar: [...nuevas].filter((id) => !previas.has(id)),
        aLiberar: [...previas].filter((id) => !nuevas.has(id)),
    };
}

/**
 * Flattens the nested join Supabase returns for a reservation's mesas
 * (`reservation_tables(mesa_id, mesas(numero))`) into a plain, sorted list.
 *
 * A row fetched before the join existed — or one whose mesas were freed — has
 * no `reservation_tables` key at all, so the absent case has to read the same
 * as the empty one: no mesas assigned.
 *
 * @param {object} row reservation row as it comes from the repository
 * @returns {Array<{ id: number, numero: number }>}
 */
export function mesasDeReserva(row) {
    return (row?.reservation_tables ?? [])
        .map((link) => ({ id: link.mesa_id, numero: link.mesas?.numero }))
        .filter((mesa) => mesa.numero != null)
        .sort((a, b) => a.numero - b.numero);
}

/** Etiqueta para una reserva sin mesas. Un guion no dice si falta el dato o si nadie asigno nada. */
export const SIN_MESA = 'Sin mesa asignada';

/**
 * Human label for a reservation's assigned mesas, for the reservation row
 * ("Mesas 7, 8 y 9"). Ordered numerically — lexicographic ordering would put
 * mesa 10 before mesa 9.
 *
 * @param {Array<{ numero: number }>} mesas
 * @returns {string}
 */
export function describeAsignacion(mesas) {
    if (!mesas || mesas.length === 0) return SIN_MESA;

    const numeros = mesas.map((mesa) => mesa.numero).sort((a, b) => a - b);
    if (numeros.length === 1) return `Mesa ${numeros[0]}`;

    const last = numeros[numeros.length - 1];
    return `Mesas ${numeros.slice(0, -1).join(', ')} y ${last}`;
}
